import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import {
    selectActiveClientIds,
    selectActiveClients
} from '~/reducers/activeClientsSlice';
import { socketUtils } from '~/utils/socket/socket-utils';
import socketInstance from '~/utils/socket/socket-instance';
import { selectAppInitialized } from '~/reducers/appGlobalsSlice';
import {
    selectSelectedClientIds,
    selectSelectedClients
} from '~/reducers/selectedClientsSlice';

interface UseWebSocket {
    areAllSelectedClientsConnected: boolean;
    connectedClientIds: string[];
}

const useWebSocket = (): UseWebSocket => {
    const activeClientIds = useSelector(selectActiveClientIds);
    const selectedClientIds = useSelector(selectSelectedClientIds);

    const areAllSelectedClientsConnected = useMemo(() => {
        return isEqual(selectedClientIds.sort(), activeClientIds.sort());
    }, [activeClientIds, selectedClientIds]);

    return {
        connectedClientIds: activeClientIds,
        areAllSelectedClientsConnected
    };
};

const WebSocketProvider = () => {
    const activeClients = useSelector(selectActiveClients);
    const selectedClients = useSelector(selectSelectedClients);
    const isAppInitialized = useSelector(selectAppInitialized);

    useEffect(() => {
        if (!isAppInitialized) {
            return;
        }

        const roomsToLeave = socketUtils.findRoomsToLeave(
            activeClients,
            selectedClients
        );
        const roomsToJoin = socketUtils.findRoomsToJoin(
            activeClients,
            selectedClients
        );

        socketInstance.leaveRooms(roomsToLeave);
        socketInstance.joinRooms(roomsToJoin);
    }, [activeClients, selectedClients, isAppInitialized]);

    return null;
};

export { WebSocketProvider, useWebSocket };
